/* eslint-disable @typescript-eslint/no-explicit-any */
import { cva } from 'class-variance-authority'
import { cn } from '~/lib/utils'
import {
  CarouselApi,
  CarouselContent,
  CarouselItem
} from '~/components/UI/shadcn/carousel'
import { useEffect, useState } from 'react'
import { useMediaQuery } from 'usehooks-ts'
import { isDevelopment } from '~/utils/misc'
import { ClientOnly } from 'remix-utils/client-only'

import CarouselContainer from '../../helpers/CarouselContainer'
import MediaCarousel from '../helpers/MediaCarousel'
import StandardText from '~/components/Text/StandardText/StandardText'
import { useQuery } from '@tanstack/react-query'
import { getEntriesByIds } from '~/lib/contensis/misc'
import Loader from '~/components/UI/General/Loader/Loader'

const carouselVariants = cva('', {
  variants: {
    backgroundColour: {
      'ink-blue': 'bg-blue-d text-white',
      'mid-blue': 'bg-blue-m text-white',
      'light-blue': 'bg-blue-l text-blue-d',
      chartreuse: 'bg-char text-blue-d',
      'light-grey': 'bg-grey-l text-blue-d',
      white: 'bg-white text-blue-d'
    },
    svgColour: {
      'ink-blue': 'fill-blue-d',
      'mid-blue': 'fill-blue-m',
      'light-blue': 'fill-blue-l',
      chartreuse: 'fill-char',
      'light-grey': 'fill-grey-l',
      white: 'fill-white'
    }
  },
  defaultVariants: {
    backgroundColour: 'mid-blue',
    svgColour: 'mid-blue'
  }
})

const FeatureBannerCarouselV2 = ({ content }: FeatureCarouselProps) => {
  const {
    backgroundColour,
    slides = [],
    urbanTexture,
    reverse,
    contained,
    loop
  } = content

  const contentBlockIds = slides
    .map((slide: any) => slide?.sys?.id)
    .filter(Boolean)

  const { data, isLoading, error } = useQuery({
    queryKey: ['carousel-items', contentBlockIds],
    queryFn: async () => {
      return contentBlockIds.length ? getEntriesByIds(contentBlockIds) : []
    },
    enabled: contentBlockIds.length > 0
  })

  const dataById = Array.isArray(data)
    ? data.reduce((acc: Record<string, any>, entry: any) => {
        acc[entry?.sys?.id] = entry
        return acc
      }, {})
    : {}

  const hydratedSlides = Array.isArray(slides)
    ? slides.map((slide: any) =>
        slide?.sys?.id ? dataById[slide.sys.id] || slide : slide
      )
    : []

  const [api, setApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(0)
  const [, setCount] = useState(0)

  const [sizeNum, setSizeNum] = useState(0)

  const isDesktop = useMediaQuery('(min-width: 1280px)', {
    defaultValue: true,
    initializeWithValue: false
  })
  const isTablet = useMediaQuery('(max-width: 1280px)', {
    defaultValue: true,
    initializeWithValue: false
  })
  const isMobile = useMediaQuery('(max-width: 640px)', {
    defaultValue: true,
    initializeWithValue: false
  })

  useEffect(() => {
    if (!api) {
      return
    }

    setCount(api.scrollSnapList().length)
    setCurrent(api.selectedScrollSnap() + 1)

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap() + 1)
    })
  }, [api])

  useEffect(() => {
    api?.scrollTo(current - 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current])

  const currentHandler = (value: number) => {
    setCurrent(value)
    api?.scrollTo(value - 1)
  }

  useEffect(() => {
    const updateSize = () => {
      if (isMobile) {
        setSizeNum(24)
      } else if (isTablet) {
        setSizeNum(32)
      } else if (isDesktop) {
        setSizeNum(48)
      }
    }

    updateSize()
  }, [isDesktop, isMobile, isTablet])

  const half = sizeNum > 0 ? `${sizeNum / 2}px` : '24px'
  const normal = sizeNum > 0 ? `${sizeNum}px` : '48px'
  const double = sizeNum > 0 ? `${sizeNum * 2}px` : '96px'

  const previewUrl =
    isDevelopment && !urbanTexture?.asset.sys.uri.startsWith('https')
      ? 'https://preview-websitemodern-uni-hull.cloud.contensis.com'
      : ''

  const foregroundColour = ['ink-blue', 'mid-blue'].includes(backgroundColour)
    ? 'white'
    : 'ink-blue'

  if (isLoading)
    return (
      <div className='py-l flex w-full justify-center'>
        <Loader fill={'#f5b6dc'} />
      </div>
    )
  if (error) return <div>Error loading component.</div>

  return (
    <ClientOnly>
      {() => (
        <div
          className={cn(
            'relative flex flex-col xl:max-h-[680px] xl:min-h-[680px]',
            contained ? 'my-l container' : '',
            reverse ? 'xl:flex-row-reverse' : 'xl:flex-row'
          )}
        >
          {slides && (
            <>
              <div className='relative w-full max-sm:mt-[18px]'>
                <MediaCarousel
                  slides={
                    Array.isArray(hydratedSlides) && hydratedSlides.length
                      ? hydratedSlides
                      : []
                  }
                  contained={contained}
                  reverse={reverse}
                  current={current}
                  setCurrent={currentHandler}
                  className={cn(
                    reverse ? 'justify-start' : 'justify-end',
                    contained
                      ? 'xl:h-full xl:[&_video]:min-h-[680px] xl:[&>div]:h-full xl:[&>div:not(.tag)>img]:min-h-[680px] xl:[&>div>div]:h-full'
                      : '',
                    'relative flex items-center max-sm:mt-[18px] xl:min-w-1/2 xl:basis-2/3 max-sm:[&_div]:max-h-[300px] xl:[&_div:not(.tag)>img]:h-[680px] [&_video]:max-h-[300px] sm:[&_video]:max-h-max xl:[&_video]:h-[680px] [&>div]:max-w-full [&>div]:overflow-visible sm:[&>div]:max-h-max sm:[&>div]:overflow-hidden xl:[&>div]:w-full'
                  )}
                  btnClasses='!bottom-m sm:!bottom-xl xl:!bottom-s'
                />
                <svg
                  className={cn(
                    carouselVariants({
                      backgroundColour: null,
                      svgColour: backgroundColour
                    }),
                    reverse ? 'xl:left-0' : 'xl:right-0 xl:-mr-[1px]',
                    'h-l sm:h-xl absolute bottom-0 z-10 flex w-full max-xl:-mb-[1px] xl:top-0 xl:h-full xl:w-auto'
                  )}
                  width={double}
                >
                  <rect
                    x={!isDesktop ? 0 : reverse ? 0 : normal}
                    y={isMobile ? 24 : isTablet ? 32 : 0}
                    width={normal}
                    height='100%'
                    style={
                      !isDesktop
                        ? { width: `calc(50% - ${half})`, height: normal }
                        : { height: `calc(50% - ${half})` }
                    }
                  />
                  <rect
                    x={!isDesktop ? 0 : reverse ? normal : 0}
                    height={normal}
                    width={normal}
                    style={
                      !isDesktop
                        ? { transform: `translateX(calc(50% - ${half}))` }
                        : { transform: `translateY(calc(50% - ${half}))` }
                    }
                  />
                  <rect
                    x={!isDesktop ? 0 : reverse ? 0 : normal}
                    y={isMobile ? 24 : isTablet ? 32 : 0}
                    width={normal}
                    height='100%'
                    style={
                      !isDesktop
                        ? {
                            width: `calc(50% - ${half})`,
                            height: normal,
                            transform: `translateX(calc(50% + ${half}))`
                          }
                        : {
                            height: `calc(50% - ${half})`,
                            transform: `translateY(calc(50% + ${half}))`
                          }
                    }
                  />
                </svg>
              </div>

              <CarouselContainer
                carouselApi={setApi}
                className={cn(
                  carouselVariants({
                    backgroundColour: backgroundColour,
                    svgColour: null
                  }),
                  'px-xs-plus py-m sm:p-l mb-s sm:mb-m xl:py-2xl xl:px-xl gap-l relative flex min-h-full flex-col xl:mb-0 xl:min-w-[calc(50%-78px)] xl:[&>div]:max-w-[650px] xl:[&>div:nth-child(2)]:grow',
                  contained
                    ? '[&>div]:max-w-full [&>div:nth-child(2)]:w-full'
                    : '',
                  reverse
                    ? 'xl:ml-m xl:-mr-[1px] 2xl:items-end'
                    : 'xl:mr-m xl:-ml-[1px]'
                )}
                options={{ loop, align: 'start' }}
                controlsColour={foregroundColour}
                hideControls={slides.length < 2 ? true : false}
                controlsClasses='w-full'
              >
                <div
                  className={cn(
                    'xl:w-m bg-blue-d -mb-s h-s sm:h-m sm:-mb-m absolute bottom-0 left-0 w-full xl:top-0 xl:mb-0 xl:h-full',
                    reverse ? 'xl:-left-m' : 'xl:-right-m xl:left-auto'
                  )}
                  style={{
                    backgroundImage: `url(${previewUrl}${urbanTexture?.asset.sys.uri}?${isDesktop ? 'crop=32,680' : 'crop=1080,32,center'}&q=75&f=webp)`,
                    backgroundSize: 'cover',
                    opacity: 1
                  }}
                ></div>

                <CarouselContent className='h-full items-center'>
                  {Array.isArray(hydratedSlides) &&
                    hydratedSlides.map((slide, index: Index) => (
                      <CarouselItem
                        key={`content-${index}`}
                        className={cn(
                          reverse ? '' : '',
                          'xl:not-last:mr-m flex'
                        )}
                      >
                        {slide && slide.contentBlock && (
                          <StandardText
                            content={{
                              ...slide.contentBlock,
                              icon: slide.icon,
                              tags: slide.tags,
                              buttons: slide.buttons
                            }}
                            backgroundColour={backgroundColour}
                            className={cn(
                              contained ? 'w-auto' : '',
                              reverse ? 'xl:self-end' : ''
                            )}
                          />
                        )}
                      </CarouselItem>
                    ))}
                  {/* {Array.isArray(slides) &&
                    slides.map(
                      (
                        slide: MediaSlideProps & {
                          contentBlock?: any
                          icon?: any
                          tags?: any
                          buttons?: any
                        },
                        index: Index
                      ) => (
                        <CarouselItem
                          key={`content-${index}`}
                          className={cn(reverse ? '' : '', 'xl:mr-m flex')}
                        >
                          {slide.contentBlock && (
                            <StandardText
                              content={{
                                ...slide.contentBlock,
                                icon: slide.icon,
                                tags: slide.tags,
                                buttons: slide.buttons
                              }}
                              backgroundColour={backgroundColour}
                              className={cn(
                                contained ? 'w-auto' : '',
                                reverse ? 'xl:self-end' : ''
                              )}
                            />
                          )}
                        </CarouselItem>
                      )
                    )} */}
                </CarouselContent>
              </CarouselContainer>
            </>
          )}
        </div>
      )}
    </ClientOnly>
  )
}

export default FeatureBannerCarouselV2
